import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Plus, Sparkles, X } from "lucide-react";
import AddonRequestDialog from "@/components/AddonRequestDialog";
import { useToast } from "@/hooks/use-toast";

interface BookingLite {
  id: string;
  user_id: string;
  service_category: string;
  price: number;
  duration: string;
  service_name: string;
}

interface Row {
  id: string;
  name: string;
  price: number;
  duration: string;
  status: "pending" | "confirmed" | "declined";
}

const statusStyles: Record<string, string> = {
  pending: "bg-amber-500/10 text-amber-600 border-amber-500/30",
  confirmed: "bg-emerald-500/10 text-emerald-600 border-emerald-500/30",
  declined: "bg-rose-500/10 text-rose-600 border-rose-500/30",
};

const ClientAddonsSection = ({ booking }: { booking: BookingLite }) => {
  const { toast } = useToast();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [removing, setRemoving] = useState<string | null>(null);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("booking_addons")
      .select("id, name, price, duration, status")
      .eq("booking_id", booking.id)
      .order("created_at", { ascending: true });
    if (error) console.error(error);
    setRows((data || []) as Row[]);
    setLoading(false);
  }, [booking.id]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const cancelPending = async (id: string) => {
    setRemoving(id);
    const { error } = await supabase.from("booking_addons").delete().eq("id", id);
    setRemoving(null);
    if (error) {
      toast({ title: "Couldn't remove", description: error.message, variant: "destructive" });
      return;
    }
    fetchRows();
  };

  return (
    <div className="border-t border-border/50 pt-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-muted-foreground">
          <Sparkles size={12} className="text-primary" /> Add-ons
        </div>
        <Button
          variant="outline"
          size="sm"
          className="rounded-none border-primary/40 text-primary hover:bg-primary/10 tracking-[0.2em] uppercase text-[10px] font-medium gap-1.5 h-7"
          onClick={() => setOpen(true)}
        >
          <Plus size={12} /> Add
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-2 text-muted-foreground">
          <Loader2 size={12} className="animate-spin" />
        </div>
      ) : rows.length === 0 ? (
        <p className="text-xs text-muted-foreground italic">No add-ons yet. Tap "Add" to request one.</p>
      ) : (
        <div className="space-y-1.5">
          {rows.map((r) => (
            <div
              key={r.id}
              className="flex items-center justify-between gap-2 p-2 border border-border/40 bg-muted/10 text-sm"
            >
              <div className="min-w-0 flex items-center gap-2 flex-wrap">
                <span className="truncate">{r.name}</span>
                <Badge
                  variant="outline"
                  className={`text-[9px] tracking-widest uppercase ${statusStyles[r.status]}`}
                >
                  {r.status}
                </Badge>
                <span className="text-[11px] text-muted-foreground">+${Number(r.price).toFixed(2)}</span>
              </div>
              {r.status === "pending" && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-6 px-1.5 text-muted-foreground"
                  disabled={removing === r.id}
                  onClick={() => cancelPending(r.id)}
                >
                  {removing === r.id ? <Loader2 size={12} className="animate-spin" /> : <X size={12} />}
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      <AddonRequestDialog
        booking={booking}
        open={open}
        onOpenChange={setOpen}
        onSubmitted={fetchRows}
      />
    </div>
  );
};

export default ClientAddonsSection;
